import React, { Fragment, useRef, useState } from "react";
import { useTable, useExpanded } from "react-table";
import { v4 } from "uuid";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  Grid,
  Typography
} from "@mui/material";

import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from "@mui/icons-material";
import dayjs from "dayjs";
import { type ReactTableColumn } from "../../types";
import { useElementMaxHeight } from "../../hooks/useTableMaxHeight";

interface CustomTableProps<T extends object> {
  className?: string;
  data: T[];
  columns: Array<ReactTableColumn<T>>;
  handleSort?: (id: string) => void;
  fontSize?: string | number;
  offset?: number;
}

const CustomTable = <T extends object>({
  className,
  data,
  columns,
  handleSort,
  fontSize = "1.4rem",
  offset
}: CustomTableProps<T>): React.ReactElement => {
  const [openRows, setOpenRows] = useState<any>([]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const height = useElementMaxHeight({ ref: containerRef as React.RefObject<HTMLElement>, offset });

  const handleRowClick = (rowIndex: any): void => {
    if (openRows.includes(rowIndex) === true) {
      setOpenRows(openRows.filter((row: any) => row !== rowIndex));
    } else {
      setOpenRows([...openRows, rowIndex]);
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } =
    useTable(
      {
        columns,
        data,
        initialState: {
          hiddenColumns: columns
            .filter((column: any) => column.hidden)
            .map((column: any) => column.accessor)
        }
      },
      useExpanded
    );

  const hasCollapsibleItem = columns.some(
    (column: any) =>
      column.hidden === true && rows.some((row: any) => row.original[column.accessor])
  );

  return (
    <TableContainer
      ref={containerRef}
      component={Paper}
      sx={{
        scrollbarWidth: "thin",
        maxHeight: {
          xs: height * 1.6,
          sm: height
        }
      }}
    >
      <Table stickyHeader {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} key={v4()}>
              {hasCollapsibleItem && <TableCell key={v4()} />}
              {headerGroup.headers.map((column) => (
                <TableCell {...column.getHeaderProps()} key={v4()}>
                  <Grid sx={{ display: "flex" }}>
                    <Typography fontSize={fontSize} color="#000000">
                      {column.render("Header")}
                    </Typography>
                    {column.id !== "selection" &&
                      column.Header !== "" &&
                      handleSort !== undefined && (
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: "16px" }}
                          onClick={() => {
                            handleSort(column.id);
                          }}
                        />
                      )}
                  </Grid>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            const isRowOpen: boolean = openRows.includes(rowIndex);
            return (
              <Fragment key={v4()}>
                <TableRow className={className} key={v4()}>
                  {hasCollapsibleItem && (
                    <TableCell
                      sx={{
                        background: isRowOpen ? "#d82d2b" : "white"
                      }}
                    >
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                          handleRowClick(rowIndex);
                        }}
                        sx={{
                          background: isRowOpen ? "white" : "",
                          color: isRowOpen ? "#d82d2b" : "",
                          "&:hover": {
                            background: isRowOpen ? "white" : "",
                            color: isRowOpen ? "#d82d2b" : ""
                          }
                        }}
                      >
                        {isRowOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                  )}
                  {row.cells.map((cell) => (
                    <TableCell
                      size="small"
                      {...cell.getCellProps()}
                      key={v4()}
                      sx={{
                        fontSize,
                        background: isRowOpen && hasCollapsibleItem ? "#d82d2b" : "white",
                        color: isRowOpen && hasCollapsibleItem ? "white" : ""
                      }}
                    >
                      {cell.render("Cell")}
                    </TableCell>
                  ))}
                </TableRow>
                {isRowOpen && hasCollapsibleItem && (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + (hasCollapsibleItem ? 1 : 0)}
                      sx={{ background: "#f5f5f5" }}
                    >
                      {/* Content inside the collapsible row */}
                      <Collapse in={true} style={{ display: "block" }}>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography
                                  variant="body1"
                                  paragraph={false}
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "1.6rem",
                                    lineHeight: "1.6rem"
                                  }}
                                  color="error"
                                >
                                  More Details
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {columns.map((column, columnIndex) => {
                              if (column.hidden === true) {
                                const accessor = column.accessor;

                                if (typeof accessor !== "string") return null;

                                const rawValue =
                                  row.original[accessor as keyof typeof row.original];
                                const isDateLike =
                                  accessor.includes("Time") || accessor.includes("Date");

                                const cellValue = isDateLike
                                  ? dayjs(String(rawValue)).format("D MMM YYYY")
                                  : rawValue;

                                return (
                                  <TableRow key={v4()}>
                                    <TableCell>
                                      <Typography fontSize={fontSize}>
                                        {typeof column.Header === "string" ? column.Header : "—"}:
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontSize={fontSize}>
                                        {String(cellValue)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                              return null;
                            })}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTable;
